import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "d-flex" }
const _hoisted_2 = { class: "py-2" }
const _hoisted_3 = {
  id: "phone_number",
  class: "my-2"
}
const _hoisted_4 = { class: "d-flex flex-row nowrap align-items-center" }
const _hoisted_5 = { class: "col-4" }
const _hoisted_6 = { class: "col-8" }

import { computed, ComputedRef, ref, onMounted } from 'vue'

  // Components
  import PageSection from '@/components/UI/PageSection.vue'
  import Input from '@/components/UI/Input.vue'
  import Autocomplete from '@/components/UI/Autocomplete.vue'

  // Config
  import countries from 'i18n-iso-countries'
  import phone_codes, { PhoneCode } from '@/config/phone_codes'

  // Types
  import { FormProps } from './EditInfoForm.types'
  import { SelectOption } from '@/components/UI/AutoComplete.types'

  // Translations
  import { useI18n } from 'vue-i18n'
  import { useStore } from '@/store'
  import InputWrapper from '@/components/UI/InputWrapper.vue'
  import InputFieldError from '@/components/UI/InputFieldError.vue'
  
export default /*@__PURE__*/_defineComponent({
  __name: 'ContactFields',
  props: {
    userInfo: {},
    formValues: {},
    updateFN: { type: Function },
    invalidFields: {},
    loading: { type: Boolean },
    editable: { type: Boolean }
  },
  setup(__props: any) {

  // Vue
  const { t } = useI18n()

  // Store
  const store = useStore()

  // Props
  const props = __props

  // Computed
  const locale: ComputedRef<string> = computed(() => {
    return store.getters.getLocale
  })

  const lang: ComputedRef<string> = computed(() => {
    return locale.value.split('-')[0] || 'en'
  })

  const selectedCountryOption: ComputedRef<SelectOption | undefined> = computed(
    () => {
      const country: SelectOption | undefined = preparedCountries.value.find(
        countryAutocompleteOption => {
          return countryAutocompleteOption.value === props.formValues.country
        }
      )

      return country
    }
  )

  const phoneCodeOptions: ComputedRef<SelectOption[]> = computed(() => {
    const options: SelectOption[] = phone_codes.map((codeObj: PhoneCode) => {
      return {
        label: `${codeObj['dial_code']} (${codeObj['code']})`,
        value: codeObj.dial_code,
      }
    })
    return options
  })

  const selectedPhoneCode: ComputedRef<SelectOption | undefined> = computed(
    () => {
      const selected = phoneCodeOptions.value.find((option: SelectOption) => {
        return option.value === props.formValues.phone_code
      })
      return selected
    }
  )

  // Refs
  const preparedCountries = ref<SelectOption[]>([])

  // Methods
  const fetchPreparedCountries = async (): Promise<void> => {
    const VIPCountryCodes = ['FR', 'BE', 'NL', 'GB']

    if (!lang.value) throw new Error('Language not defined in document.')

    const countriesForLocale = await import(
      `i18n-iso-countries/langs/${lang.value}.json`
    )
    if (!countriesForLocale)
      throw new Error(`Country JSON doesn't exist for lang: ${lang.value}`)

    countries.registerLocale(countriesForLocale)

    const countryEntries = Object.entries(countriesForLocale.countries)

    const options = countryEntries.map(([code, names]) => {
      const label = Array.isArray(names) ? names.join(' / ') : names
      return {
        value: code,
        label,
      } as SelectOption
    })

    // Move VIP countries to the top
    const sortedOptions = options.sort(
      (a, b) =>
        Number(VIPCountryCodes.includes(b.value as string)) -
        Number(VIPCountryCodes.includes(a.value as string))
    )

    // Reorder specific positions
    if (sortedOptions.length >= 3)
      [sortedOptions[0], sortedOptions[2]] = [
        sortedOptions[2],
        sortedOptions[0],
      ]
    if (sortedOptions.length >= 4)
      sortedOptions.splice(1, 0, sortedOptions.splice(3, 1)[0])

    preparedCountries.value = sortedOptions
  }

  const convertCountryNameToCode = (
    countryName: string
  ): string | undefined => {
    const formattedCountryName = countryName.includes('/')
      ? countryName.split('/')[0].trim()
      : countryName
    return countries.getAlpha2Code(formattedCountryName, lang.value)
  }

  const handlePhoneCodeChange = (option: SelectOption) => {
    if (!option.value)
      throw new Error('invalid option in handlePhoneCodeChange')
    const value = option.value
    props.updateFN('phone_code', value as string)
  }

  const handleCountryChange = (option: SelectOption) => {
    const value = convertCountryNameToCode(option.label as string)
    if (!value) throw new Error('invalid value in handleCountryChange')
    props.updateFN('country', value)
  }

  // Validate initial phone number on component mount
  const readonlyStyles = computed(() => ({
    border: 'none',
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
  }))

  onMounted(async () => {
    await fetchPreparedCountries()
  })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(PageSection, {
    title: _unref(t)('profile.editInfoForm.contactsTitle')
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", null, [
        _createVNode(Input, {
          id: "address",
          label: _unref(t)('profile.editInfoForm.address'),
          placeholder: _unref(t)('profile.editInfoForm.address'),
          value: _ctx.formValues.address,
          onInput: _cache[0] || (_cache[0] = (value) => _ctx.updateFN('address', value)),
          error: _unref(t)('errorMessages.fieldMandatory'),
          "show-error": _ctx.invalidFields.has('address'),
          readOnly: !_ctx.editable || _ctx.loading,
          "input-styles": !_ctx.editable ? readonlyStyles.value : {}
        }, null, 8, ["label", "placeholder", "value", "error", "show-error", "readOnly", "input-styles"]),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(Input, {
            class: "col-6 py-2 pe-1",
            id: "postal-code",
            label: _unref(t)('profile.editInfoForm.postalCode'),
            placeholder: _unref(t)('profile.editInfoForm.postalCode'),
            value: _ctx.formValues.postal_code,
            onInput: _cache[1] || (_cache[1] = (value) => _ctx.updateFN('postal_code', value)),
            error: _unref(t)('errorMessages.fieldMandatory'),
            "show-error": _ctx.invalidFields.has('postal_code'),
            readOnly: !_ctx.editable || _ctx.loading,
            "input-styles": !_ctx.editable ? readonlyStyles.value : {}
          }, null, 8, ["label", "placeholder", "value", "error", "show-error", "readOnly", "input-styles"]),
          _createVNode(Input, {
            class: "col-6 py-2 ps-1",
            id: "city",
            label: _unref(t)('profile.editInfoForm.city'),
            placeholder: _unref(t)('profile.editInfoForm.city'),
            value: _ctx.formValues.city,
            onInput: _cache[2] || (_cache[2] = (value) => _ctx.updateFN('city', value)),
            error: _unref(t)('errorMessages.fieldMandatory'),
            "show-error": _ctx.invalidFields.has('city'),
            readOnly: !_ctx.editable || _ctx.loading,
            "input-styles": !_ctx.editable ? readonlyStyles.value : {}
          }, null, 8, ["label", "placeholder", "value", "error", "show-error", "readOnly", "input-styles"])
        ]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(Autocomplete, {
            placeholder: _unref(t)('profile.editInfoForm.country'),
            label: _unref(t)('profile.editInfoForm.country'),
            options: preparedCountries.value,
            "selected-option": selectedCountryOption.value,
            "filter-fields": ['value', 'label'],
            onSelectClick: handleCountryChange,
            disabled: !_ctx.editable || _ctx.loading,
            "input-styles": !_ctx.editable ? readonlyStyles.value : {}
          }, null, 8, ["placeholder", "label", "options", "selected-option", "disabled", "input-styles"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(InputWrapper, {
            style: {"overflow":"visible"},
            class: _normalizeClass(_ctx.invalidFields.has('phone_number') && 'error-outline')
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(Autocomplete, {
                    id: "phone-code",
                    placeholder: '',
                    label: _unref(t)('profile.editInfoForm.phoneCode'),
                    options: phoneCodeOptions.value,
                    "selected-option": selectedPhoneCode.value,
                    "filter-fields": ['value', 'label'],
                    containerBorderRadius: '10px 0px 0px 10px',
                    inputClass: 'px-0 px-md-3 ',
                    onSelectClick: handlePhoneCodeChange,
                    disabled: !_ctx.editable || _ctx.loading,
                    readOnly: !_ctx.editable || _ctx.loading,
                    "input-styles": !_ctx.editable ? readonlyStyles.value : {}
                  }, null, 8, ["label", "options", "selected-option", "disabled", "readOnly", "input-styles"])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(Input, {
                    class: "",
                    label: _unref(t)('profile.editInfoForm.phoneNumber'),
                    placeholder: '00 00 00 00 00',
                    value: _ctx.formValues.phone_number,
                    "allowed-type": 'number',
                    onInput: _cache[3] || (_cache[3] = 
                  (value) => {
                    const onlyNumbers = /^[0-9]*$/
                    if (onlyNumbers.test(value)) {
                      console.log('Phone number input changed:', value)
                      _ctx.updateFN('phone_number', value)
                    } else {
                      console.warn('Invalid phone number input:', value)
                    }
                  }
                ),
                    containerBorderRadius: '0px 10px 10px 0px',
                    readOnly: !_ctx.editable || _ctx.loading,
                    "input-styles": 
                  !_ctx.editable
                    ? readonlyStyles.value
                    : {
                        border: 'none',
                        height: '56px',
                      }
                ,
                    "input-type": "tel"
                  }, null, 8, ["label", "value", "readOnly", "input-styles"])
                ])
              ])
            ]),
            _: 1
          }, 8, ["class"]),
          (_ctx.invalidFields.has('phone_number'))
            ? (_openBlock(), _createBlock(InputFieldError, {
                key: 0,
                text: _unref(t)('loginRegister.user_info_invalid_phone')
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})